import React from 'react';
import {
	DeprecatedContentContainer as ContentContainer,
	DeprecatedText as Text,
	deprecatedTheme as theme,
	FlexColumn,
	FlexItem,
	FlexRow,
	Icon,
} from '@forecast-it/design-system';
import {colors, spacing} from '@forecast-it/design-system/src/deprecated/shared/styles';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {NUMBER_TYPE} from '../../../../components/new-ui/project/project-budget-v3/util/BudgetUtils';
import Util from '../../../shared/util/util';
import DeprecatedForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {ForecastTooltipFormulaRenderer} from '../../../../components/ForecastTooltipFormulaRenderer';
import ProgramBudgetTypeInfo from './ProgramBudgetTypeInfo';
import {PROGRAM_BUDGET_TYPE} from '../../../../constants';
import ProgramBudgetTypeProfitBox from './ProgramBudgetTypeProfitBox';
import {hasTopDownProgramBudgetFeature} from '../../../shared/util/ProgramFinancialLogic';
import ProgramUtil from '../../../shared/util/ProgramUtil';
import {ProgramHeaderSections} from './ProgramHeaderSections';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';

const HorizontalDivider = styled.div`
	border: solid 1px ${colors.border.primary.default};
	height: 1px;
	width: ${spacing.xl};
`;

const VerticalDivider = styled.div`
	border-left: 1px solid ${theme.colors.border.primary.default};
	height: 100%;
`;

const formatNumber = (value, numberType, currencySymbol, intl) => {
	switch (numberType) {
		case NUMBER_TYPE.MONEY:
			return Util.getFormattedNumberWithCurrency(currencySymbol, value, intl);
		case NUMBER_TYPE.PERCENTAGE:
			return Util.getFormattedPercentage(value, intl);
		default:
			break;
	}
	return value;
};

const getTooltipItem = (tooltip, formatMessage) => {
	switch (tooltip) {
		case 'RevenueRecognitionAtCompletion':
			return [
				{
					title: formatMessage({id: 'project_budget.recognition_at_completion'}),
					details: [
						formatMessage({id: 'common.revenue_recognized_to_date'}),
						'+',
						formatMessage({id: 'common.forecast_revenue_recognition_to_complete'}),
					],
				},
				!hasTopDownProgramBudgetFeature()
					? {
							description: formatMessage(
								{id: 'project_budget.recognition_at_completion_tooltip'},
								{program: ProgramUtil.programText(formatMessage).toLowerCase()}
							),
					  }
					: {},
			];
		case 'RevenueRecognizedToDate':
			return [
				!hasTopDownProgramBudgetFeature()
					? {
							title: formatMessage({id: 'project_budget.recognised_to_date'}),
							description: formatMessage(
								{id: 'project_budget.recognised_to_date_tooltip'},
								{program: ProgramUtil.programText(formatMessage).toLowerCase()}
							),
					  }
					: {},
			];
		case 'SpendTotalAtCompletion':
			return [
				{
					title: formatMessage({id: 'project_budget.total_value_of_service_at_completion'}),
					details: [
						formatMessage({id: 'common.time'}),
						'x',
						formatMessage({id: 'common.rate'}),
						'+',
						formatMessage({id: 'common.expenses'}),
					],
				},
				!hasTopDownProgramBudgetFeature()
					? {
							description: formatMessage({id: 'project_budget.total_value_of_service_at_completion_tooltip'}),
					  }
					: {},
			];
		case 'SpendActualToDate':
			return [
				{
					title: formatMessage({id: 'project_budget.actual_value_of_service_to_date'}),
					details: [
						formatMessage({id: 'common.time'}),
						'x',
						formatMessage({id: 'common.rate'}),
						'+',
						formatMessage({id: 'common.expenses'}) + ' (to date)',
					],
				},
				!hasTopDownProgramBudgetFeature()
					? {
							description: formatMessage({id: 'project_budget.actual_value_of_service_to_date_tooltip'}),
					  }
					: {},
			];
		case 'ProfitMarginAtCompletion':
			return [
				{
					title: formatMessage({id: 'project_budget.profit_margin_at_completion'}),
					details: [
						`( ${formatMessage({id: 'common.revenue_recognition_at_completion'})}`,
						'-',
						`${formatMessage({id: 'common.cost_at_completion'})})`,
						'/',
						formatMessage({id: 'common.revenue_recognition_at_completion'}),
						'X',
						'100',
					],
				},
				!hasTopDownProgramBudgetFeature()
					? {
							description: formatMessage(
								{id: 'project_budget.aggregated_projects_in_program_tooltip'},
								{program: ProgramUtil.programText(formatMessage).toLowerCase()}
							),
					  }
					: {},
			];
		case 'ProfitMarginActualToDate':
			return [
				{
					title: formatMessage({id: 'project_budget.profit_margin_to_date'}),
					details: [
						`( ${formatMessage({id: 'common.revenue_recognized_to_date'})}`,
						'-',
						`${formatMessage({id: 'common.cost_to_date'})})`,
						'/',
						formatMessage({id: 'common.revenue_recognized_to_date'}),
						'X',
						'100',
					],
				},
				!hasTopDownProgramBudgetFeature()
					? {
							description: formatMessage(
								{id: 'project_budget.aggregated_projects_in_program_tooltip'},
								{program: ProgramUtil.programText(formatMessage).toLowerCase()}
							),
					  }
					: {},
			];
		case 'ProfitMarginActualAtCompletion':
			return [
				{
					title: formatMessage({id: 'project_budget.profit_margin_at_completion'}),
					details: [
						`( ${formatMessage({id: 'common.revenue_recognition_at_completion'})}`,
						'-',
						`${formatMessage({id: 'common.cost_at_completion'})})`,
						'/',
						formatMessage({id: 'common.revenue_recognition_at_completion'}),
						'X',
						'100',
					],
				},
				!hasTopDownProgramBudgetFeature()
					? {
							description: formatMessage(
								{id: 'project_budget.aggregated_projects_in_program_tooltip'},
								{program: ProgramUtil.programText(formatMessage).toLowerCase()}
							),
					  }
					: {},
			];
		case 'RevenueRecognitionAtCompletionCapVariance':
			return [
				{
					title: formatMessage({id: 'program_budget.variance_with_cap'}),
					details: [
						formatMessage({id: 'program.program_cap_amount'}),
						'-',
						formatMessage({id: 'project_budget.recognition_at_completion'}),
					],
				},
			];
		case 'RevenueRecognizedToDateCapVariance':
			return [
				{
					title: formatMessage({id: 'program_budget.variance_with_cap'}),
					details: [
						formatMessage({id: 'program.program_cap_amount'}),
						'-',
						formatMessage({id: 'common.recognition_to_date'}),
					],
				},
			];
		case 'RemainingRevenue':
			return [
				{
					title: formatMessage({id: 'project_budget.remaining_revenue'}),
					details: [
						formatMessage({id: 'common.fixed_price'}),
						'-',
						formatMessage({id: 'common.revenue_recognition_to_date'}),
					],
				},
			];
		case 'SpendActualToDateFixedPriceVariance':
			return [
				{
					title: formatMessage({id: 'program_budget.variance_with_fixed_price'}),
					details: [
						formatMessage({id: 'common.fixed_price'}),
						'-',
						formatMessage({id: 'project_budget.value_of_service_to_date'}),
					],
				},
			];
		case 'SpendTotalAtCompletionFixedPriceVariance':
			return [
				{
					title: formatMessage({id: 'program_budget.variance_with_fixed_price'}),
					details: [
						formatMessage({id: 'common.fixed_price'}),
						'-',
						formatMessage({id: 'project_budget.value_of_service_at_completion'}),
					],
				},
			];
		case 'SpendActualToDateCapVariance':
			return [
				{
					title: formatMessage({id: 'program_budget.variance_with_cap'}),
					details: [
						formatMessage({id: 'program.program_cap_amount'}),
						'-',
						formatMessage({id: 'project_budget.value_of_service_to_date'}),
					],
				},
			];
		case 'SpendTotalAtCompletionCapVariance':
			return [
				{
					title: formatMessage({id: 'program_budget.variance_with_cap'}),
					details: [
						formatMessage({id: 'program.program_cap_amount'}),
						'-',
						formatMessage({id: 'project_budget.value_of_service_at_completion'}),
					],
				},
			];
	}
};

const BudgetHeaderCardWrapper = styled.div`
	margin-right: ${spacing.l};
`;

export const BudgetHeaderCard = ({children}) => {
	return (
		<BudgetHeaderCardWrapper>
			<FlexColumn gap={'xl'}>
				{children.map(child => {
					return <FlexItem>{child}</FlexItem>;
				})}
			</FlexColumn>
		</BudgetHeaderCardWrapper>
	);
};

export const FinancialNumber = ({
	translationId,
	cy,
	tooltip, // This can be removed once financial_categories_update is rolled out
	tooltipMaxWidth, // This can be removed once financial_categories_update is rolled out
	value,
	size = 'm',
	numberType,
	currencySymbol,
	tooltipComponent: Tooltip,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	const tooltipItem = hasFinancialCategoriesFeature ? [] : getTooltipItem(tooltip, formatMessage);

	return (
		<FlexColumn gap={'m'}>
			{hasTopDownProgramBudgetFeature() ? (
				<Text type={'label'} variant={'l'} color={'primary'}>
					{formatMessage({id: translationId})}
				</Text>
			) : (
				<Text type={'heading'} variant={'s'} color={'secondary'}>
					{formatMessage({id: translationId})}
				</Text>
			)}
			<FlexRow gap={'m'}>
				<Text
					type={'heading'}
					variant={size}
					color={value < 0 ? 'destructive' : size !== 'm' ? 'primary' : 'secondary'}
					data-cy={cy}
				>
					{formatNumber(value, numberType, currencySymbol, intl)}
				</Text>
				{!hasTopDownProgramBudgetFeature() || tooltip !== 'RevenueRecognizedToDate' || hasFinancialCategoriesFeature ? (
					hasFinancialCategoriesFeature ? (
						<Tooltip>
							<Icon icon={'help'} size={size} />
						</Tooltip>
					) : (
						<DeprecatedForecastTooltip
							maxWidth={tooltipMaxWidth}
							placement={'bottom'}
							content={<ForecastTooltipFormulaRenderer items={tooltipItem} translatedMessage={true} />}
						>
							<Icon icon={'help'} size={size} />
						</DeprecatedForecastTooltip>
					)
				) : null}
			</FlexRow>
		</FlexColumn>
	);
};

const ProgramBudgetTotals = ({program, company, actualPersonId}) => {
	const currencySymbol = Util.GetCurrencySymbol(company.currency);
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return hasTopDownProgramBudgetFeature() ? (
		<FlexColumn gap={'xl'}>
			<Text type={'heading'} variant={'l'}>
				{formatMessage({id: 'program.budget.overview'}, {Program: ProgramUtil.programText(formatMessage)})}
			</Text>

			{program.budgetType === PROGRAM_BUDGET_TYPE.UNCAPPED ? (
				<ProgramBudgetTypeInfo program={program} company={company} actualPersonId={actualPersonId} />
			) : null}

			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE || program.budgetType === PROGRAM_BUDGET_TYPE.CAPPED ? (
				<FlexRow justifyContent={'left'} gap={'l'}>
					<FlexItem>
						<ProgramBudgetTypeInfo program={program} company={company} actualPersonId={actualPersonId} />
					</FlexItem>
					<FlexItem>
						<ProgramBudgetTypeProfitBox program={program} />
					</FlexItem>
				</FlexRow>
			) : null}

			{program.budgetType === PROGRAM_BUDGET_TYPE.UNCAPPED ? (
				<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
					<ContentContainer width={'auto'}>
						<ProgramHeaderSections.ProfitSection program={program} />
					</ContentContainer>
					<ContentContainer width={'auto'}>
						<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
							<ProgramHeaderSections.RevenueSection program={program} currencySymbol={currencySymbol} />
							<VerticalDivider />
							<ProgramHeaderSections.ValueOfServiceSection program={program} currencySymbol={currencySymbol} />
						</FlexRow>
					</ContentContainer>
				</FlexRow>
			) : null}

			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE || program.budgetType === PROGRAM_BUDGET_TYPE.CAPPED ? (
				<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
					<ContentContainer width={'auto'}>
						<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
							{program.budgetType === PROGRAM_BUDGET_TYPE.CAPPED ? (
								<>
									<ProgramHeaderSections.ActualRevenueSection
										program={program}
										currencySymbol={currencySymbol}
									/>
									<VerticalDivider />
									<ProgramHeaderSections.ProjectedTotalRevenueSection
										program={program}
										currencySymbol={currencySymbol}
									/>
								</>
							) : null}
							{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE ? (
								<ProgramHeaderSections.RevenueSection program={program} currencySymbol={currencySymbol} />
							) : null}
							<VerticalDivider />
							<ProgramHeaderSections.ActualValueOfServiceSection
								program={program}
								currencySymbol={currencySymbol}
							/>
							<VerticalDivider />
							<ProgramHeaderSections.ProjectedTotalValueOfServiceSection
								program={program}
								currencySymbol={currencySymbol}
							/>
						</FlexRow>
					</ContentContainer>
				</FlexRow>
			) : null}
		</FlexColumn>
	) : (
		<FlexColumn gap={'xl'}>
			<Text type={'heading'} variant={'l'}>
				{formatMessage({id: 'program.budget.overview'}, {Program: ProgramUtil.programText(formatMessage)})}
			</Text>

			<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
				<ContentContainer width={'auto'}>
					<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
						<BudgetHeaderCard>
							<Text type={'heading'} variant={'m'} color={'primary'}>
								{formatMessage({id: 'project_budget.revenue'})}
							</Text>
							<FinancialNumber
								translationId={
									hasFinancialCategoriesFeature
										? 'common.projected_total_revenue'
										: 'project_budget.recognition_at_completion'
								}
								tooltip={'RevenueRecognitionAtCompletion'}
								tooltipMaxWidth={450}
								value={program.projectsFinancialsTotals.recognitionForecastRevenue}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
								size={'l'}
							/>
							<HorizontalDivider />
							<FinancialNumber
								translationId={'project_budget.recognised_to_date'}
								tooltip={'RevenueRecognizedToDate'}
								tooltipMaxWidth={350}
								value={program.projectsFinancialsToDate.recognitionAmount}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
							/>
						</BudgetHeaderCard>
						<BudgetHeaderCard>
							<Text type={'heading'} variant={'m'} color={'primary'}>
								{formatMessage({
									id: hasFinancialCategoriesFeature
										? 'common.actual_value_of_service'
										: 'project_budget.value_of_service_to_date',
								})}
							</Text>
							<FinancialNumber
								translationId={
									hasFinancialCategoriesFeature
										? 'common.projected_total_value_of_service'
										: 'project_budget.value_of_service_at_completion'
								}
								tooltip={'SpendTotalAtCompletion'}
								tooltipMaxWidth={350}
								value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
								size={'l'}
							/>
							<HorizontalDivider />
							<FinancialNumber
								translationId={
									hasFinancialCategoriesFeature
										? 'common.actual_value_of_service'
										: 'project_budget.value_of_service_to_date'
								}
								tooltip={'SpendActualToDate'}
								tooltipMaxWidth={350}
								value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
							/>
						</BudgetHeaderCard>
					</FlexRow>
				</ContentContainer>
				<ContentContainer width={'auto'}>
					<BudgetHeaderCard>
						<Text type={'heading'} variant={'m'} color={'primary'}>
							{formatMessage({id: 'project_budget.profit_margin'})}
						</Text>
						<FinancialNumber
							translationId={
								hasFinancialCategoriesFeature
									? 'common.projected_total_margin'
									: 'project_budget.profit_margin_at_completion'
							}
							tooltip={'ProfitMarginAtCompletion'}
							tooltipMaxWidth={1000}
							value={program.projectsFinancialsTotals.recognitionProfitPercentage}
							numberType={NUMBER_TYPE.PERCENTAGE}
							size={'l'}
						/>
						<HorizontalDivider />
						<FinancialNumber
							translationId={
								hasFinancialCategoriesFeature
									? 'project_budget.actual_margin'
									: 'project_budget.profit_margin_to_date'
							}
							tooltip={'ProfitMarginActualToDate'}
							tooltipMaxWidth={1000}
							value={program.projectsFinancialsToDate.recognitionActualProfitPercentage}
							numberType={NUMBER_TYPE.PERCENTAGE}
						/>
					</BudgetHeaderCard>
				</ContentContainer>
			</FlexRow>
		</FlexColumn>
	);
};

export default ProgramBudgetTotals;
